'use client'

import classes from './reset-password-form.module.css'

import { useActionState, useEffect, useState, useTransition } from 'react'

import { Button, PasswordInput, PinInput, Stack, Text, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { z } from 'zod'

import { routes } from '@/constants/routes'
import { RESET_PASSWORD_INITIAL_STATE } from '@/lib/api/auth/auth.constants'
import { resetPassword } from '@/lib/api/auth/auth.service'
import type {
  ResetPasswordFormData,
  ResetPasswordFormState,
  ResetPasswordMutationData,
} from '@/lib/api/auth/auth.types'
import { showSuccess } from '@/lib/notifications'
import { ZodPasswordCheck } from '@/utils/zod'

import FormErrorMessage from '../components/form-error-message'

const CODE_CONFIRMATION_LENGTH = 6

type ResetPasswordFormProps = {
  userEmail: string
}

const ResetPasswordForm = (props: ResetPasswordFormProps) => {
  const { userEmail } = props

  const router = useRouter()
  const t = useTranslations('auth')
  const [, startTransition] = useTransition()
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)

  const schema = z
    .object({
      code: z.string().min(1, {
        error: t('reset-password.form.fields.confirmation-code.error.required'),
      }),
      password: ZodPasswordCheck(t, 'reset-password'),
      confirmPassword: ZodPasswordCheck(t, 'reset-password'),
    })
    .refine((data) => data.password === data.confirmPassword, {
      error: t('reset-password.form.fields.password-confirmation.error.match'),
      path: ['confirmPassword'],
    })

  const form = useForm<ResetPasswordFormData>({
    mode: 'controlled',
    initialValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
    validate: zod4Resolver(schema),
  })

  const [state, submitAction, isSubmitting] = useActionState<
    ResetPasswordFormState,
    ResetPasswordMutationData
  >(resetPassword, RESET_PASSWORD_INITIAL_STATE)

  const handleSubmit = (values: ResetPasswordFormData) => {
    startTransition(() => {
      submitAction({ ...values, username: userEmail })
    })
  }

  useEffect(() => {
    if (state.success) {
      showSuccess({
        title: t('reset-password.notifications.password-updated.title'),
        message: t('reset-password.notifications.password-updated.description'),
      })
      router.push(routes.login)
    }
  }, [state.success, t, router])

  console.log(state)

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
      <Stack gap='xl'>
        <Stack gap='xs' align='center'>
          <Title size='h2' fz='xl' ta='center'>
            {t('reset-password.title')}
          </Title>
          <Text fz='xs' ta='center' w='70%'>
            {t('reset-password.description')}
          </Text>
        </Stack>
        <Stack gap='lg'>
          <Stack gap='md'>
            <Stack
              gap='xs'
              opacity={isSubmitting ? 0.2 : 1}
              style={{
                pointerEvents: isSubmitting ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-out',
              }}
            >
              <Text fz='xs' fw={500}>
                {t('reset-password.form.fields.confirmation-code.label')}
              </Text>
              <Stack className={classes.containerCodeConfirmation}>
                <PinInput
                  error={!!form.errors.code}
                  ff='monospace'
                  fw='500'
                  length={CODE_CONFIRMATION_LENGTH}
                  size='md'
                  w='100%'
                  style={{
                    justifyContent: 'space-between',
                  }}
                  value={form.values.code}
                  onChange={(value) => form.setFieldValue('code', value)}
                />
                {form.errors.code && (
                  <Text c='var(--mantine-color-error)' size='xs'>
                    (!) {form.errors.code}
                  </Text>
                )}
              </Stack>
            </Stack>

            <PasswordInput
              description={t('reset-password.form.fields.password.description')}
              label={t('reset-password.form.fields.password.label')}
              onVisibilityChange={setIsPasswordVisible}
              opacity={isSubmitting ? 0.2 : 1}
              placeholder={t('reset-password.form.fields.password.placeholder')}
              required
              size='xs'
              style={{
                pointerEvents: isSubmitting ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-out',
              }}
              visible={isPasswordVisible}
              {...form.getInputProps('password')}
            />

            <PasswordInput
              label={t('reset-password.form.fields.password-confirmation.label')}
              onVisibilityChange={setIsPasswordVisible}
              opacity={isSubmitting ? 0.2 : 1}
              placeholder={t(
                'reset-password.form.fields.password-confirmation.placeholder',
              )}
              required
              size='xs'
              style={{
                pointerEvents: isSubmitting ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-out',
              }}
              visible={isPasswordVisible}
              {...form.getInputProps('confirmPassword')}
            />
          </Stack>

          <Button
            autoContrast
            loading={isSubmitting}
            loaderProps={{ type: 'dots' }}
            type='submit'
            size='xs'
          >
            {t('reset-password.form.submit.label')}
          </Button>
        </Stack>
        <FormErrorMessage message={state.errorMessage} />
      </Stack>
    </form>
  )
}

export default ResetPasswordForm
